import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Flame, Plus, Search, Globe, FileText, Zap, TrendingUp, 
  Settings, BarChart3, Download, Play, Pause, Trash2, Eye,
  RefreshCw, CheckCircle, Clock, AlertCircle, Layers, Target,
  ArrowRight, ArrowLeft, Link2, Scan, Rocket, Map, Tag, 
  Building2, ExternalLink, Copy, Check, ChevronRight, Sparkles, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Campaign status colors
const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  paused: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  generating: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  deployed: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

// Wizard Steps
const WIZARD_STEPS = [
  { id: 1, title: 'Domain Setup', icon: Globe, description: 'Configure your target domain' },
  { id: 2, title: 'Destination', icon: Link2, description: 'Where should traffic go?' },
  { id: 3, title: 'Website Scan', icon: Scan, description: 'Extract branding & content' },
  { id: 4, title: 'Targeting', icon: Target, description: 'Location & keyword targeting' },
  { id: 5, title: 'Products/Services', icon: Tag, description: 'What are you promoting?' },
  { id: 6, title: 'Launch', icon: Rocket, description: 'Review & deploy campaign' },
];

const SEOFurnace = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  
  // Campaign wizard data
  const [campaignData, setCampaignData] = useState({
    // Step 1: Domain
    name: '',
    domain: '',
    hasDomain: 'yes',
    
    // Step 2: Destination
    destinationUrl: '',
    destinationType: 'external', // 'external' or 'internal'
    
    // Step 3: Scanned data
    businessName: '',
    businessDescription: '',
    brandColors: { primary: '#0ea5e9', secondary: '#f97316' },
    logo: '',
    
    // Step 4: Targeting
    targetingScope: 'local', // 'local', 'state', 'national'
    locations: '',
    
    // Step 5: Products/Services
    keywords: '',
    services: '',
    
    // Step 6: Settings
    pagesToGenerate: 100,
    includeAiContent: true,
    generateSitemap: true,
    generateLegalPages: true,
  });

  // Stats
  const [stats, setStats] = useState({
    total_campaigns: 0,
    total_pages: 0,
    indexed_pages: 0,
    total_leads: 0,
  });

  const handleLogin = () => {
    if (password === 'furnace2024') {
      setIsAuthenticated(true);
      toast.success('Welcome to SEO Furnace 🔥');
      fetchCampaigns();
      fetchStats();
    } else {
      toast.error('Invalid password');
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/furnace/campaigns`);
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/furnace/stats`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  // Scan destination website for branding
  const scanWebsite = async () => {
    if (!campaignData.destinationUrl) {
      toast.error('Please enter a destination URL');
      return;
    }
    
    setScanning(true);
    try {
      // Simulate scanning - in production this would call an API
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Extract domain name for business name suggestion
      const url = new URL(campaignData.destinationUrl.startsWith('http') ? campaignData.destinationUrl : `https://${campaignData.destinationUrl}`);
      const domainParts = url.hostname.replace('www.', '').split('.');
      const suggestedName = domainParts[0].charAt(0).toUpperCase() + domainParts[0].slice(1);
      
      setScannedData({
        businessName: suggestedName,
        description: `Professional services provided by ${suggestedName}`,
        colors: { primary: '#0ea5e9', secondary: '#f97316' },
      });
      
      setCampaignData(prev => ({
        ...prev,
        businessName: suggestedName,
        businessDescription: `Professional services provided by ${suggestedName}`,
      }));
      
      toast.success('Website scanned successfully!');
    } catch (error) {
      toast.error('Failed to scan website. Please enter details manually.');
    } finally {
      setScanning(false);
    }
  };

  const createCampaign = async () => {
    setLoading(true);
    try {
      const keywordsArray = campaignData.keywords.split('\n').filter(k => k.trim());
      const locationsArray = campaignData.locations.split('\n').filter(l => l.trim());
      const servicesArray = campaignData.services.split('\n').filter(s => s.trim());
      
      const res = await fetch(`${API_URL}/api/furnace/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: campaignData.name,
          domain: campaignData.domain,
          destination_url: campaignData.destinationUrl,
          business_name: campaignData.businessName,
          business_description: campaignData.businessDescription,
          targeting_scope: campaignData.targetingScope,
          keywords: keywordsArray,
          locations: locationsArray,
          services: servicesArray,
          pages_to_generate: campaignData.pagesToGenerate,
          include_ai_snippets: campaignData.includeAiContent,
          generate_sitemap: campaignData.generateSitemap,
          generate_about: campaignData.generateLegalPages,
          generate_contact: campaignData.generateLegalPages,
          generate_privacy: campaignData.generateLegalPages,
        }),
      });
      
      if (res.ok) {
        toast.success('Campaign created! 🔥 Ready to generate pages.');
        setShowWizard(false);
        resetWizard();
        fetchCampaigns();
        fetchStats();
      } else {
        toast.error('Failed to create campaign');
      }
    } catch (error) {
      toast.error('Error creating campaign');
    } finally {
      setLoading(false);
    }
  };

  const startGeneration = async (campaignId) => {
    try {
      toast.loading('Starting page generation...', { id: 'generate' });
      const res = await fetch(`${API_URL}/api/furnace/campaigns/${campaignId}/generate`, {
        method: 'POST',
      });
      if (res.ok) {
        toast.success('Page generation started! 🔥', { id: 'generate' });
        fetchCampaigns();
      } else {
        toast.error('Failed to start generation', { id: 'generate' });
      }
    } catch (error) {
      toast.error('Failed to start generation', { id: 'generate' });
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setCampaignData({
      name: '',
      domain: '',
      hasDomain: 'yes',
      destinationUrl: '',
      destinationType: 'external',
      businessName: '',
      businessDescription: '',
      brandColors: { primary: '#0ea5e9', secondary: '#f97316' },
      logo: '',
      targetingScope: 'local',
      locations: '',
      keywords: '',
      services: '',
      pagesToGenerate: 100,
      includeAiContent: true,
      generateSitemap: true,
      generateLegalPages: true,
    });
    setScannedData(null);
  };

  const nextStep = () => {
    if (wizardStep < 6) setWizardStep(wizardStep + 1);
  };

  const prevStep = () => {
    if (wizardStep > 1) setWizardStep(wizardStep - 1);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-center justify-center p-4">
        <Helmet>
          <title>SEO Furnace | Login</title>
        </Helmet>
        
        {/* Animated background flames */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 w-full max-w-md border border-orange-500/20 shadow-2xl shadow-orange-500/10">
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-orange-500/50 animate-pulse">
              <Flame className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 mb-2">
              SEO FURNACE
            </h1>
            <p className="text-slate-400">Incinerate the Competition 🔥</p>
          </div>
          
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Enter access code"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className="bg-slate-900/50 border-orange-500/30 text-white placeholder:text-slate-500 h-12 text-center text-lg"
            />
            <Button 
              onClick={handleLogin} 
              className="w-full h-12 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-white font-bold text-lg shadow-lg shadow-orange-500/30"
            >
              <Flame className="w-5 h-5 mr-2" />
              IGNITE
            </Button>
          </div>
          
          <div className="mt-8 pt-6 border-t border-slate-700 text-center">
            <p className="text-slate-500 text-sm mb-2">Powered by</p>
            <p className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
              LIONHEART DIGITAL
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Helmet>
        <title>SEO Furnace | Dashboard</title>
      </Helmet>

      {/* Header */}
      <header className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 py-4 px-6 shadow-lg shadow-orange-500/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <Flame className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black">SEO FURNACE</h1>
                <p className="text-xs text-white/70">Incinerate the Competition</p>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={() => { resetWizard(); setShowWizard(true); }}
            className="bg-white text-orange-600 hover:bg-orange-50 font-bold shadow-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Campaign
          </Button>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-slate-800/50 border-b border-slate-700 py-6 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl p-5 border border-orange-500/20">
            <div className="flex items-center gap-2 text-orange-400 text-sm mb-2">
              <Layers className="w-5 h-5" />
              Active Campaigns
            </div>
            <div className="text-4xl font-black text-white">{stats.total_campaigns}</div>
          </div>
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-5 border border-blue-500/20">
            <div className="flex items-center gap-2 text-blue-400 text-sm mb-2">
              <FileText className="w-5 h-5" />
              Pages Generated
            </div>
            <div className="text-4xl font-black text-white">{stats.total_pages}</div>
          </div>
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl p-5 border border-green-500/20">
            <div className="flex items-center gap-2 text-green-400 text-sm mb-2">
              <CheckCircle className="w-5 h-5" />
              Indexed by Google
            </div>
            <div className="text-4xl font-black text-green-400">{stats.indexed_pages}</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-5 border border-purple-500/20">
            <div className="flex items-center gap-2 text-purple-400 text-sm mb-2">
              <Target className="w-5 h-5" />
              Leads Captured
            </div>
            <div className="text-4xl font-black text-purple-400">{stats.total_leads}</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => { resetWizard(); setShowWizard(true); }}
            className="group bg-gradient-to-br from-orange-500/20 to-red-500/20 hover:from-orange-500/30 hover:to-red-500/30 rounded-2xl p-6 border border-orange-500/30 text-left transition-all"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Rocket className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Launch Campaign</h3>
            <p className="text-slate-400 text-sm">Create a new promotional campaign with our step-by-step wizard</p>
          </button>
          
          <button
            onClick={() => setActiveTab('campaigns')}
            className="group bg-gradient-to-br from-blue-500/20 to-cyan-500/20 hover:from-blue-500/30 hover:to-cyan-500/30 rounded-2xl p-6 border border-blue-500/30 text-left transition-all"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <BarChart3 className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">View Campaigns</h3>
            <p className="text-slate-400 text-sm">Monitor and manage your active SEO campaigns</p>
          </button>
          
          <button
            className="group bg-gradient-to-br from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 rounded-2xl p-6 border border-purple-500/30 text-left transition-all"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Download className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Export Leads</h3>
            <p className="text-slate-400 text-sm">Download captured leads as CSV for follow-up</p>
          </button>
        </div>

        {/* Campaigns List */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              Your Campaigns
            </h2>
            <Button variant="outline" size="sm" onClick={fetchCampaigns} className="border-slate-600">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {loading ? (
            <div className="text-center py-12 text-slate-400">
              <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-orange-500" />
              Loading campaigns...
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Flame className="w-10 h-10 text-slate-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">No campaigns yet</h3>
              <p className="text-slate-400 mb-6">Create your first campaign to start generating traffic</p>
              <Button onClick={() => { resetWizard(); setShowWizard(true); }} className="bg-gradient-to-r from-orange-500 to-red-500">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Campaign
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="p-6 hover:bg-slate-800/50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-bold">{campaign.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[campaign.status]}`}>
                          {campaign.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          {campaign.domain}
                        </span>
                        {campaign.destination_url && (
                          <span className="flex items-center gap-1">
                            <ExternalLink className="w-4 h-4" />
                            {campaign.destination_url}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => startGeneration(campaign.id)}
                        className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Generate
                      </Button>
                      <Button size="sm" variant="outline" className="border-slate-600">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-orange-400">{campaign.pages_generated || 0}</div>
                      <div className="text-xs text-slate-500">Pages</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-green-400">{campaign.pages_indexed || 0}</div>
                      <div className="text-xs text-slate-500">Indexed</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-blue-400">{campaign.keywords?.length || 0}</div>
                      <div className="text-xs text-slate-500">Keywords</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-purple-400">{campaign.leads || 0}</div>
                      <div className="text-xs text-slate-500">Leads</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Campaign Creation Wizard */}
      {showWizard && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-slate-800 rounded-3xl w-full max-w-3xl border border-slate-700 shadow-2xl my-8">
            {/* Wizard Header */}
            <div className="px-8 py-6 border-b border-slate-700">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Launch Campaign</h2>
                    <p className="text-slate-400 text-sm">Step {wizardStep} of 6</p>
                  </div>
                </div>
                <button onClick={() => setShowWizard(false)} className="text-slate-400 hover:text-white text-2xl">
                  ×
                </button>
              </div>
              
              {/* Progress Steps */}
              <div className="flex items-center justify-between">
                {WIZARD_STEPS.map((step, idx) => {
                  const StepIcon = step.icon;
                  const isActive = wizardStep === step.id;
                  const isComplete = wizardStep > step.id;
                  
                  return (
                    <React.Fragment key={step.id}>
                      <div className={`flex flex-col items-center ${isActive ? 'text-orange-400' : isComplete ? 'text-green-400' : 'text-slate-600'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                          isActive ? 'bg-orange-500/20 border-2 border-orange-500' : 
                          isComplete ? 'bg-green-500/20 border-2 border-green-500' : 
                          'bg-slate-700 border-2 border-slate-600'
                        }`}>
                          {isComplete ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                        </div>
                        <span className="text-xs hidden sm:block">{step.title}</span>
                      </div>
                      {idx < WIZARD_STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-2 ${wizardStep > step.id ? 'bg-green-500' : 'bg-slate-700'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            
            {/* Wizard Content */}
            <div className="p-8">
              {/* Step 1: Domain Setup */}
              {wizardStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Globe className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Domain Setup</h3>
                    <p className="text-slate-400">Configure the domain for your SEO campaign</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Campaign Name</label>
                    <Input
                      placeholder="e.g., Sydney Plumbers Campaign"
                      value={campaignData.name}
                      onChange={(e) => setCampaignData({...campaignData, name: e.target.value})}
                      className="bg-slate-900 border-slate-600 h-12"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Do you have a domain for this campaign?</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setCampaignData({...campaignData, hasDomain: 'yes'})}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          campaignData.hasDomain === 'yes' 
                            ? 'border-orange-500 bg-orange-500/10' 
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        <CheckCircle className={`w-6 h-6 mb-2 ${campaignData.hasDomain === 'yes' ? 'text-orange-400' : 'text-slate-500'}`} />
                        <div className="font-semibold">Yes, I have a domain</div>
                        <div className="text-sm text-slate-400">I'll enter my existing domain</div>
                      </button>
                      <button
                        onClick={() => setCampaignData({...campaignData, hasDomain: 'no'})}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          campaignData.hasDomain === 'no' 
                            ? 'border-orange-500 bg-orange-500/10' 
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        <Plus className={`w-6 h-6 mb-2 ${campaignData.hasDomain === 'no' ? 'text-orange-400' : 'text-slate-500'}`} />
                        <div className="font-semibold">No, I need one</div>
                        <div className="text-sm text-slate-400">Help me set up a domain</div>
                      </button>
                    </div>
                  </div>
                  
                  {campaignData.hasDomain === 'yes' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Your Domain</label>
                      <Input
                        placeholder="e.g., sydneyplumbers.com.au"
                        value={campaignData.domain}
                        onChange={(e) => setCampaignData({...campaignData, domain: e.target.value})}
                        className="bg-slate-900 border-slate-600 h-12"
                      />
                    </div>
                  )}
                  
                  {campaignData.hasDomain === 'no' && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                      <h4 className="font-semibold text-blue-400 mb-2">Domain Registration</h4>
                      <p className="text-slate-300 text-sm mb-3">
                        We recommend registering a domain through Namecheap or GoDaddy. 
                        Once registered, enter it above and we'll help you configure it.
                      </p>
                      <a 
                        href="https://www.namecheap.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
                      >
                        Register at Namecheap <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Destination */}
              {wizardStep === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Link2 className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Destination Website</h3>
                    <p className="text-slate-400">Where should SEO Furnace drive traffic to?</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Destination URL</label>
                    <Input
                      placeholder="e.g., https://www.yourbusiness.com.au"
                      value={campaignData.destinationUrl}
                      onChange={(e) => setCampaignData({...campaignData, destinationUrl: e.target.value})}
                      className="bg-slate-900 border-slate-600 h-12"
                    />
                    <p className="text-slate-500 text-sm mt-2">
                      This is the website visitors will be directed to when they click through from your doorway pages
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3: Website Scan */}
              {wizardStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Scan className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Scan & Extract</h3>
                    <p className="text-slate-400">We'll extract branding and content from your destination website</p>
                  </div>
                  
                  {!scannedData ? (
                    <div className="text-center py-8">
                      <Button 
                        onClick={scanWebsite}
                        disabled={scanning}
                        className="bg-gradient-to-r from-orange-500 to-red-500 h-14 px-8 text-lg"
                      >
                        {scanning ? (
                          <>
                            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                            Scanning website...
                          </>
                        ) : (
                          <>
                            <Scan className="w-5 h-5 mr-2" />
                            Scan {campaignData.destinationUrl || 'Website'}
                          </>
                        )}
                      </Button>
                      <p className="text-slate-500 text-sm mt-4">
                        Or enter the details manually below
                      </p>
                    </div>
                  ) : (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                      <div className="flex items-center gap-2 text-green-400 mb-2">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">Website scanned successfully!</span>
                      </div>
                      <p className="text-slate-300 text-sm">We've extracted some information. Feel free to edit below.</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Business Name</label>
                    <Input
                      placeholder="e.g., Sydney Emergency Plumbers"
                      value={campaignData.businessName}
                      onChange={(e) => setCampaignData({...campaignData, businessName: e.target.value})}
                      className="bg-slate-900 border-slate-600 h-12"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Business Description</label>
                    <textarea
                      placeholder="Brief description for the homepage and meta tags..."
                      value={campaignData.businessDescription}
                      onChange={(e) => setCampaignData({...campaignData, businessDescription: e.target.value})}
                      className="w-full h-24 px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Targeting */}
              {wizardStep === 4 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Target className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Geographic Targeting</h3>
                    <p className="text-slate-400">Where should SEO Furnace target traffic?</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">Targeting Scope</label>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { id: 'local', label: 'Local', desc: 'Specific suburbs/cities', icon: MapPin },
                        { id: 'state', label: 'State-wide', desc: 'Entire state coverage', icon: Map },
                        { id: 'national', label: 'National', desc: 'All of Australia', icon: Globe },
                      ].map(({ id, label, desc, icon: Icon }) => (
                        <button
                          key={id}
                          onClick={() => setCampaignData({...campaignData, targetingScope: id})}
                          className={`p-4 rounded-xl border-2 text-center transition-all ${
                            campaignData.targetingScope === id 
                              ? 'border-orange-500 bg-orange-500/10' 
                              : 'border-slate-600 hover:border-slate-500'
                          }`}
                        >
                          <Icon className={`w-8 h-8 mx-auto mb-2 ${campaignData.targetingScope === id ? 'text-orange-400' : 'text-slate-500'}`} />
                          <div className="font-semibold">{label}</div>
                          <div className="text-xs text-slate-400">{desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Target Locations (one per line)
                    </label>
                    <textarea
                      placeholder="Sydney&#10;Melbourne&#10;Brisbane&#10;Gold Coast&#10;Surfers Paradise&#10;..."
                      value={campaignData.locations}
                      onChange={(e) => setCampaignData({...campaignData, locations: e.target.value})}
                      className="w-full h-32 px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Step 5: Products/Services */}
              {wizardStep === 5 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Tag className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Products & Services</h3>
                    <p className="text-slate-400">What keywords and services should we target?</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Services/Products (one per line)
                    </label>
                    <textarea
                      placeholder="Emergency Plumbing&#10;Blocked Drains&#10;Hot Water Systems&#10;Bathroom Renovations&#10;..."
                      value={campaignData.services}
                      onChange={(e) => setCampaignData({...campaignData, services: e.target.value})}
                      className="w-full h-28 px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Keywords - Short & Long Tail (one per line)
                    </label>
                    <textarea
                      placeholder="emergency plumber&#10;24 hour plumber near me&#10;blocked drain repair service&#10;hot water system installation&#10;..."
                      value={campaignData.keywords}
                      onChange={(e) => setCampaignData({...campaignData, keywords: e.target.value})}
                      className="w-full h-32 px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                    />
                    <p className="text-slate-500 text-sm mt-2">
                      Pages will be created: domain.com/[location]/[keyword]
                    </p>
                  </div>
                </div>
              )}

              {/* Step 6: Launch */}
              {wizardStep === 6 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Rocket className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Ready to Launch! 🔥</h3>
                    <p className="text-slate-400">Review your campaign and prepare for liftoff</p>
                  </div>
                  
                  {/* Campaign Summary */}
                  <div className="bg-slate-900/50 rounded-xl p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-slate-500 text-sm">Campaign Name</span>
                        <p className="font-semibold">{campaignData.name || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Domain</span>
                        <p className="font-semibold">{campaignData.domain || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Destination</span>
                        <p className="font-semibold truncate">{campaignData.destinationUrl || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Targeting</span>
                        <p className="font-semibold capitalize">{campaignData.targetingScope}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Locations</span>
                        <p className="font-semibold">{campaignData.locations.split('\n').filter(l => l.trim()).length} locations</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Keywords</span>
                        <p className="font-semibold">{campaignData.keywords.split('\n').filter(k => k.trim()).length} keywords</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Generation Settings */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Pages to Generate: <span className="text-orange-400 font-bold">{campaignData.pagesToGenerate}</span>
                    </label>
                    <input
                      type="range"
                      min="25"
                      max="500"
                      step="25"
                      value={campaignData.pagesToGenerate}
                      onChange={(e) => setCampaignData({...campaignData, pagesToGenerate: parseInt(e.target.value)})}
                      className="w-full accent-orange-500"
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>25 pages</span>
                      <span>500 pages</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        checked={campaignData.includeAiContent}
                        onChange={(e) => setCampaignData({...campaignData, includeAiContent: e.target.checked})}
                        className="rounded border-slate-600 text-orange-500 focus:ring-orange-500"
                      />
                      <div>
                        <div className="font-medium flex items-center gap-1">
                          <Sparkles className="w-4 h-4 text-orange-400" />
                          AI Content
                        </div>
                        <div className="text-xs text-slate-400">Generate unique content with AI</div>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        checked={campaignData.generateSitemap}
                        onChange={(e) => setCampaignData({...campaignData, generateSitemap: e.target.checked})}
                        className="rounded border-slate-600 text-orange-500 focus:ring-orange-500"
                      />
                      <div>
                        <div className="font-medium flex items-center gap-1">
                          <FileText className="w-4 h-4 text-blue-400" />
                          Sitemap
                        </div>
                        <div className="text-xs text-slate-400">Auto-generate XML sitemap</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
            
            {/* Wizard Footer */}
            <div className="px-8 py-6 border-t border-slate-700 flex items-center justify-between">
              <Button 
                variant="outline" 
                onClick={wizardStep === 1 ? () => setShowWizard(false) : prevStep}
                className="border-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {wizardStep === 1 ? 'Cancel' : 'Back'}
              </Button>
              
              {wizardStep < 6 ? (
                <Button 
                  onClick={nextStep}
                  className="bg-gradient-to-r from-orange-500 to-red-500"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={createCampaign}
                  disabled={loading}
                  className="bg-gradient-to-r from-orange-500 to-red-500 px-8"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Launch Campaign 🔥
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
              SEO FURNACE
            </span>
          </div>
          <p className="text-slate-500 text-sm mb-2">Powered by</p>
          <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-yellow-500">
            LIONHEART DIGITAL
          </p>
          <p className="text-slate-600 text-sm italic mt-2">"They lit the fire. We became it."</p>
        </div>
      </footer>
    </div>
  );
};

export default SEOFurnace;
